/**
 * Psychometric Assessment Question Bank
 *
 * 32 questions across 3 sections. Every one of the 49 traits (T01–T49) is covered.
 * Each question option carries traitWeights — values 1–10 added to each trait's running sum.
 * No answer is wrong; options simply activate different trait clusters with different intensities.
 *
 * Scoring: normalise per-trait raw sum against the maximum possible raw sum for that trait
 * (see traitScoring.ts) to produce a 0-100 score for every trait.
 */

export type TraitWeight = {
  traitId: string; // e.g. "T01"
  value: number;   // 1–10
};

export type QuestionOption = {
  id: string;          // "A" | "B" | "C" | "D" | "E"
  text: string;
  traitWeights: TraitWeight[];
};

export type Question = {
  id: string;          // "Q01"…"Q32"
  section: 1 | 2 | 3;
  text: string;
  options: QuestionOption[];
};

// ---------------------------------------------------------------------------
// SECTION 1 — "How You Think"  (12 questions)
// Focus: analytical, logical, strategic, systems, STEM-cognitive traits
// ---------------------------------------------------------------------------

const section1: Question[] = [
  {
    id: "Q01",
    section: 1,
    text: "A new app you installed keeps crashing for no obvious reason. What's your first move?",
    options: [
      {
        id: "A",
        text: "Open the error logs and trace through exactly what happened step by step",
        traitWeights: [{ traitId: "T01", value: 8 }, { traitId: "T02", value: 7 }, { traitId: "T13", value: 6 }, { traitId: "T47", value: 6 }],
      },
      {
        id: "B",
        text: "Try to reproduce the crash in different ways until I spot the pattern",
        traitWeights: [{ traitId: "T09", value: 8 }, { traitId: "T11", value: 6 }, { traitId: "T01", value: 5 }, { traitId: "T46", value: 5 }],
      },
      {
        id: "C",
        text: "Think about how the app's components connect and where the fault might ripple",
        traitWeights: [{ traitId: "T06", value: 8 }, { traitId: "T08", value: 7 }, { traitId: "T34", value: 5 }, { traitId: "T02", value: 4 }],
      },
      {
        id: "D",
        text: "Ask someone who's seen similar issues, piece together a solution from their experience",
        traitWeights: [{ traitId: "T18", value: 6 }, { traitId: "T17", value: 6 }, { traitId: "T43", value: 6 }, { traitId: "T46", value: 4 }],
      },
    ],
  },
  {
    id: "Q02",
    section: 1,
    text: "You're handed a spreadsheet of sales numbers, dates, and customer types. What do you naturally do first?",
    options: [
      {
        id: "A",
        text: "Scan for patterns and trends — what's going up, what's going down",
        traitWeights: [{ traitId: "T09", value: 8 }, { traitId: "T10", value: 7 }, { traitId: "T07", value: 5 }, { traitId: "T37", value: 6 }],
      },
      {
        id: "B",
        text: "Question what the data is really measuring — is it telling the full story?",
        traitWeights: [{ traitId: "T11", value: 8 }, { traitId: "T01", value: 7 }, { traitId: "T43", value: 7 }, { traitId: "T10", value: 4 }],
      },
      {
        id: "C",
        text: "Mentally visualize it — try to imagine a chart or graph of the data",
        traitWeights: [{ traitId: "T03", value: 7 }, { traitId: "T20", value: 6 }, { traitId: "T09", value: 5 }, { traitId: "T07", value: 4 }],
      },
      {
        id: "D",
        text: "Think about how to present this to someone who needs to make a decision with it",
        traitWeights: [{ traitId: "T17", value: 7 }, { traitId: "T34", value: 6 }, { traitId: "T35", value: 5 }, { traitId: "T37", value: 5 }],
      },
    ],
  },
  {
    id: "Q03",
    section: 1,
    text: "Your school has limited funds to fix three major problems. You're asked to help decide which to prioritise. What do you focus on?",
    options: [
      {
        id: "A",
        text: "Analyse each problem's impact using numbers and hard evidence",
        traitWeights: [{ traitId: "T01", value: 7 }, { traitId: "T35", value: 8 }, { traitId: "T36", value: 7 }, { traitId: "T37", value: 6 }],
      },
      {
        id: "B",
        text: "Talk to students and teachers — understand which problem affects people most",
        traitWeights: [{ traitId: "T14", value: 7 }, { traitId: "T15", value: 8 }, { traitId: "T19", value: 6 }, { traitId: "T17", value: 5 }],
      },
      {
        id: "C",
        text: "Think about which fix creates the best long-term outcome for the school",
        traitWeights: [{ traitId: "T34", value: 8 }, { traitId: "T39", value: 7 }, { traitId: "T06", value: 5 }, { traitId: "T32", value: 5 }],
      },
      {
        id: "D",
        text: "Find a creative single solution that tackles multiple problems at once",
        traitWeights: [{ traitId: "T16", value: 8 }, { traitId: "T46", value: 7 }, { traitId: "T08", value: 5 }, { traitId: "T32", value: 6 }],
      },
    ],
  },
  {
    id: "Q04",
    section: 1,
    text: "There's a maths puzzle no one in your class can solve. What do you do?",
    options: [
      {
        id: "A",
        text: "Break it into smaller pieces and work through each piece logically",
        traitWeights: [{ traitId: "T01", value: 8 }, { traitId: "T02", value: 8 }, { traitId: "T07", value: 7 }, { traitId: "T47", value: 5 }],
      },
      {
        id: "B",
        text: "Look for a hidden pattern or mathematical shortcut",
        traitWeights: [{ traitId: "T09", value: 8 }, { traitId: "T08", value: 7 }, { traitId: "T07", value: 6 }, { traitId: "T02", value: 4 }],
      },
      {
        id: "C",
        text: "Try a completely wild approach — even if it seems unlikely to work at first",
        traitWeights: [{ traitId: "T16", value: 6 }, { traitId: "T40", value: 7 }, { traitId: "T12", value: 7 }, { traitId: "T08", value: 5 }],
      },
      {
        id: "D",
        text: "Work through it patiently, step by step, until something finally clicks",
        traitWeights: [{ traitId: "T49", value: 8 }, { traitId: "T48", value: 7 }, { traitId: "T02", value: 5 }, { traitId: "T47", value: 6 }],
      },
    ],
  },
  {
    id: "Q05",
    section: 1,
    text: "You need to explain how a car engine works to a 10-year-old. How do you approach it?",
    options: [
      {
        id: "A",
        text: "Use an analogy — 'it's like a stomach that digests fuel to give the car energy'",
        traitWeights: [{ traitId: "T17", value: 7 }, { traitId: "T30", value: 7 }, { traitId: "T16", value: 6 }, { traitId: "T08", value: 4 }],
      },
      {
        id: "B",
        text: "Draw a diagram showing the parts and how they connect to each other",
        traitWeights: [{ traitId: "T03", value: 7 }, { traitId: "T20", value: 7 }, { traitId: "T05", value: 6 }, { traitId: "T29", value: 5 }],
      },
      {
        id: "C",
        text: "Find or build a simple physical model they can hold and see",
        traitWeights: [{ traitId: "T05", value: 8 }, { traitId: "T25", value: 7 }, { traitId: "T04", value: 6 }, { traitId: "T29", value: 6 }],
      },
      {
        id: "D",
        text: "Ask what the child already knows and build the explanation from their interest",
        traitWeights: [{ traitId: "T30", value: 8 }, { traitId: "T14", value: 7 }, { traitId: "T15", value: 6 }, { traitId: "T23", value: 5 }],
      },
    ],
  },
  {
    id: "Q06",
    section: 1,
    text: "You notice that a specific move in a game you're playing always seems to lead to winning. What do you do?",
    options: [
      {
        id: "A",
        text: "Test it repeatedly in a controlled way to confirm it's a real pattern",
        traitWeights: [{ traitId: "T09", value: 8 }, { traitId: "T11", value: 7 }, { traitId: "T01", value: 6 }, { traitId: "T02", value: 5 }],
      },
      {
        id: "B",
        text: "Try to figure out the logical reason WHY it works",
        traitWeights: [{ traitId: "T02", value: 8 }, { traitId: "T08", value: 7 }, { traitId: "T06", value: 5 }, { traitId: "T10", value: 4 }],
      },
      {
        id: "C",
        text: "Share it with others and see if they notice other patterns too",
        traitWeights: [{ traitId: "T17", value: 6 }, { traitId: "T18", value: 6 }, { traitId: "T12", value: 5 }, { traitId: "T28", value: 4 }],
      },
      {
        id: "D",
        text: "Get excited and try applying it to other games or real-life situations",
        traitWeights: [{ traitId: "T12", value: 8 }, { traitId: "T40", value: 6 }, { traitId: "T16", value: 5 }, { traitId: "T08", value: 5 }],
      },
    ],
  },
  {
    id: "Q07",
    section: 1,
    text: "Your group project is falling apart — two members disagree, and the deadline is tomorrow. You:",
    options: [
      {
        id: "A",
        text: "Step in immediately, assign everyone clear tasks and set a timeline",
        traitWeights: [{ traitId: "T33", value: 8 }, { traitId: "T38", value: 8 }, { traitId: "T35", value: 7 }, { traitId: "T28", value: 5 }],
      },
      {
        id: "B",
        text: "Listen carefully to both sides, then propose a compromise that uses both ideas",
        traitWeights: [{ traitId: "T14", value: 7 }, { traitId: "T23", value: 7 }, { traitId: "T35", value: 6 }, { traitId: "T17", value: 7 }],
      },
      {
        id: "C",
        text: "Stay calm, keep working on my section, and quietly help others where possible",
        traitWeights: [{ traitId: "T44", value: 7 }, { traitId: "T41", value: 6 }, { traitId: "T48", value: 6 }, { traitId: "T24", value: 5 }],
      },
      {
        id: "D",
        text: "Rethink the whole project structure if the current approach clearly isn't working",
        traitWeights: [{ traitId: "T34", value: 7 }, { traitId: "T39", value: 6 }, { traitId: "T01", value: 5 }, { traitId: "T46", value: 5 }],
      },
    ],
  },
  {
    id: "Q08",
    section: 1,
    text: "You're designing something — a room layout, a poster, or a website. What matters most to you?",
    options: [
      {
        id: "A",
        text: "That everything is logically placed and makes immediate sense to the viewer",
        traitWeights: [{ traitId: "T47", value: 7 }, { traitId: "T02", value: 5 }, { traitId: "T20", value: 5 }, { traitId: "T06", value: 4 }],
      },
      {
        id: "B",
        text: "That it looks beautiful and creates a strong emotional reaction",
        traitWeights: [{ traitId: "T21", value: 8 }, { traitId: "T20", value: 7 }, { traitId: "T16", value: 7 }, { traitId: "T22", value: 6 }],
      },
      {
        id: "C",
        text: "That it's functional — every element serves a clear purpose",
        traitWeights: [{ traitId: "T04", value: 7 }, { traitId: "T06", value: 5 }, { traitId: "T47", value: 6 }, { traitId: "T38", value: 4 }],
      },
      {
        id: "D",
        text: "That it reflects a unique idea or tells a story no one else would think of",
        traitWeights: [{ traitId: "T22", value: 8 }, { traitId: "T16", value: 7 }, { traitId: "T39", value: 6 }, { traitId: "T21", value: 5 }],
      },
    ],
  },
  {
    id: "Q09",
    section: 1,
    text: "Someone gives you a complex task with almost no instructions. You:",
    options: [
      {
        id: "A",
        text: "Research the topic thoroughly before writing a single line or doing anything",
        traitWeights: [{ traitId: "T11", value: 8 }, { traitId: "T12", value: 5 }, { traitId: "T43", value: 6 }, { traitId: "T47", value: 6 }],
      },
      {
        id: "B",
        text: "Dive right in, learn as I go, and adjust based on what I discover",
        traitWeights: [{ traitId: "T40", value: 8 }, { traitId: "T12", value: 7 }, { traitId: "T41", value: 6 }, { traitId: "T24", value: 5 }],
      },
      {
        id: "C",
        text: "Map out a plan first — break it into phases and milestones",
        traitWeights: [{ traitId: "T38", value: 8 }, { traitId: "T34", value: 7 }, { traitId: "T01", value: 6 }, { traitId: "T48", value: 6 }],
      },
      {
        id: "D",
        text: "Ask clarifying questions until I fully understand what's actually expected",
        traitWeights: [{ traitId: "T17", value: 7 }, { traitId: "T11", value: 5 }, { traitId: "T43", value: 6 }, { traitId: "T47", value: 5 }],
      },
    ],
  },
  {
    id: "Q10",
    section: 1,
    text: "Your friend built something and it completely broke. What do you do first?",
    options: [
      {
        id: "A",
        text: "Examine it carefully to figure out exactly what failed and where",
        traitWeights: [{ traitId: "T47", value: 8 }, { traitId: "T01", value: 7 }, { traitId: "T43", value: 6 }, { traitId: "T05", value: 5 }],
      },
      {
        id: "B",
        text: "Think about redesigning the whole thing so it won't fail next time",
        traitWeights: [{ traitId: "T06", value: 7 }, { traitId: "T34", value: 6 }, { traitId: "T04", value: 5 }, { traitId: "T39", value: 5 }],
      },
      {
        id: "C",
        text: "Offer emotional support first — let them vent — then help fix it",
        traitWeights: [{ traitId: "T14", value: 8 }, { traitId: "T23", value: 7 }, { traitId: "T15", value: 6 }, { traitId: "T24", value: 4 }],
      },
      {
        id: "D",
        text: "Suggest building a better version together from the ground up",
        traitWeights: [{ traitId: "T18", value: 6 }, { traitId: "T33", value: 5 }, { traitId: "T46", value: 6 }, { traitId: "T41", value: 5 }],
      },
    ],
  },
  {
    id: "Q11",
    section: 1,
    text: "You're learning something new and it's really difficult. What keeps you going?",
    options: [
      {
        id: "A",
        text: "The satisfaction I'll feel when I finally crack something that seemed impossible",
        traitWeights: [{ traitId: "T12", value: 7 }, { traitId: "T41", value: 7 }, { traitId: "T46", value: 6 }, { traitId: "T28", value: 5 }],
      },
      {
        id: "B",
        text: "Knowing there's a method — if I follow the steps carefully, I'll get there eventually",
        traitWeights: [{ traitId: "T48", value: 8 }, { traitId: "T02", value: 6 }, { traitId: "T31", value: 6 }, { traitId: "T49", value: 6 }],
      },
      {
        id: "C",
        text: "The knowledge that this skill could genuinely help other people",
        traitWeights: [{ traitId: "T19", value: 7 }, { traitId: "T14", value: 6 }, { traitId: "T30", value: 6 }, { traitId: "T42", value: 5 }],
      },
      {
        id: "D",
        text: "Pure curiosity — I'm deeply interested in where this knowledge leads",
        traitWeights: [{ traitId: "T12", value: 9 }, { traitId: "T08", value: 6 }, { traitId: "T11", value: 5 }, { traitId: "T04", value: 4 }],
      },
    ],
  },
  {
    id: "Q12",
    section: 1,
    text: "A problem has no clear single answer. Multiple solutions could work. You:",
    options: [
      {
        id: "A",
        text: "Accept the ambiguity and work with probabilities — pick the highest-chance option",
        traitWeights: [{ traitId: "T10", value: 8 }, { traitId: "T36", value: 7 }, { traitId: "T35", value: 6 }, { traitId: "T02", value: 5 }],
      },
      {
        id: "B",
        text: "Keep digging until I find something closer to a definitive answer",
        traitWeights: [{ traitId: "T11", value: 8 }, { traitId: "T43", value: 7 }, { traitId: "T48", value: 6 }, { traitId: "T12", value: 5 }],
      },
      {
        id: "C",
        text: "Consult multiple perspectives and synthesize the best parts of each view",
        traitWeights: [{ traitId: "T32", value: 8 }, { traitId: "T17", value: 6 }, { traitId: "T14", value: 5 }, { traitId: "T45", value: 5 }],
      },
      {
        id: "D",
        text: "Trust my instinct and make a call — I can always course-correct later",
        traitWeights: [{ traitId: "T35", value: 7 }, { traitId: "T40", value: 7 }, { traitId: "T28", value: 7 }, { traitId: "T41", value: 4 }],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// SECTION 2 — "How You Feel & Connect"  (10 questions)
// Focus: emotional, social, creative, Humanities traits
// ---------------------------------------------------------------------------

const section2: Question[] = [
  {
    id: "Q13",
    section: 2,
    text: "A classmate is clearly upset about their grade. You notice before they say anything. What do you do?",
    options: [
      {
        id: "A",
        text: "Check in with them privately and listen without offering advice unless asked",
        traitWeights: [{ traitId: "T14", value: 9 }, { traitId: "T23", value: 8 }, { traitId: "T18", value: 6 }, { traitId: "T15", value: 5 }],
      },
      {
        id: "B",
        text: "Give them space but make it clear I'm available if they need me",
        traitWeights: [{ traitId: "T23", value: 7 }, { traitId: "T24", value: 5 }, { traitId: "T49", value: 6 }, { traitId: "T14", value: 5 }],
      },
      {
        id: "C",
        text: "Encourage them to speak to the teacher — I help them frame what to say",
        traitWeights: [{ traitId: "T30", value: 6 }, { traitId: "T17", value: 6 }, { traitId: "T19", value: 5 }, { traitId: "T28", value: 4 }],
      },
      {
        id: "D",
        text: "Distract them with something fun to lift the mood first",
        traitWeights: [{ traitId: "T18", value: 7 }, { traitId: "T16", value: 5 }, { traitId: "T25", value: 4 }, { traitId: "T22", value: 4 }],
      },
    ],
  },
  {
    id: "Q14",
    section: 2,
    text: "When you have a strong opinion, how do you prefer to share it?",
    options: [
      {
        id: "A",
        text: "Calmly and with clear evidence — I want it to be rational and convincing",
        traitWeights: [{ traitId: "T17", value: 8 }, { traitId: "T28", value: 7 }, { traitId: "T02", value: 5 }, { traitId: "T42", value: 5 }],
      },
      {
        id: "B",
        text: "Passionately — I want others to feel what I feel, not just understand it",
        traitWeights: [{ traitId: "T22", value: 8 }, { traitId: "T28", value: 7 }, { traitId: "T17", value: 6 }, { traitId: "T26", value: 4 }],
      },
      {
        id: "C",
        text: "I prefer to listen first — I only share if asked or if no one else has",
        traitWeights: [{ traitId: "T23", value: 6 }, { traitId: "T49", value: 6 }, { traitId: "T14", value: 5 }, { traitId: "T27", value: 5 }],
      },
      {
        id: "D",
        text: "I frame it as a question — I'd rather spark discussion than declare a position",
        traitWeights: [{ traitId: "T17", value: 7 }, { traitId: "T18", value: 6 }, { traitId: "T30", value: 6 }, { traitId: "T12", value: 5 }],
      },
    ],
  },
  {
    id: "Q15",
    section: 2,
    text: "In any group you join, what role do you naturally drift into?",
    options: [
      {
        id: "A",
        text: "The organiser — I like making sure everyone knows what they're doing",
        traitWeights: [{ traitId: "T33", value: 9 }, { traitId: "T38", value: 7 }, { traitId: "T28", value: 7 }, { traitId: "T48", value: 5 }],
      },
      {
        id: "B",
        text: "The peacekeeper — I make sure everyone feels heard and included",
        traitWeights: [{ traitId: "T14", value: 8 }, { traitId: "T18", value: 8 }, { traitId: "T23", value: 7 }, { traitId: "T19", value: 5 }],
      },
      {
        id: "C",
        text: "The ideas person — I generate creative directions and get people excited",
        traitWeights: [{ traitId: "T16", value: 8 }, { traitId: "T22", value: 7 }, { traitId: "T12", value: 6 }, { traitId: "T39", value: 5 }],
      },
      {
        id: "D",
        text: "The researcher — I dig up the facts and make sure the group's work is accurate",
        traitWeights: [{ traitId: "T11", value: 7 }, { traitId: "T43", value: 7 }, { traitId: "T47", value: 6 }, { traitId: "T01", value: 5 }],
      },
    ],
  },
  {
    id: "Q16",
    section: 2,
    text: "When you go through a really hard personal experience, what do you typically do?",
    options: [
      {
        id: "A",
        text: "Process it internally first — I need to think it through alone before talking",
        traitWeights: [{ traitId: "T23", value: 7 }, { traitId: "T24", value: 7 }, { traitId: "T44", value: 6 }, { traitId: "T49", value: 5 }],
      },
      {
        id: "B",
        text: "Talk it out with someone I completely trust",
        traitWeights: [{ traitId: "T17", value: 6 }, { traitId: "T14", value: 5 }, { traitId: "T18", value: 6 }, { traitId: "T23", value: 4 }],
      },
      {
        id: "C",
        text: "Find a creative outlet — write about it, make music, draw, or build something",
        traitWeights: [{ traitId: "T22", value: 8 }, { traitId: "T26", value: 7 }, { traitId: "T16", value: 6 }, { traitId: "T21", value: 5 }],
      },
      {
        id: "D",
        text: "Focus entirely on what I can control and keep myself moving forward",
        traitWeights: [{ traitId: "T41", value: 8 }, { traitId: "T44", value: 7 }, { traitId: "T48", value: 7 }, { traitId: "T24", value: 5 }],
      },
    ],
  },
  {
    id: "Q17",
    section: 2,
    text: "When you imagine your ideal impact on the world, it looks like:",
    options: [
      {
        id: "A",
        text: "Solving a large-scale problem that improves life for many people",
        traitWeights: [{ traitId: "T19", value: 8 }, { traitId: "T46", value: 7 }, { traitId: "T39", value: 7 }, { traitId: "T34", value: 5 }],
      },
      {
        id: "B",
        text: "Creating something — art, music, technology — that moves people deeply",
        traitWeights: [{ traitId: "T16", value: 8 }, { traitId: "T22", value: 8 }, { traitId: "T21", value: 7 }, { traitId: "T26", value: 4 }],
      },
      {
        id: "C",
        text: "Leading an organisation or movement toward a meaningful goal",
        traitWeights: [{ traitId: "T33", value: 8 }, { traitId: "T39", value: 8 }, { traitId: "T34", value: 7 }, { traitId: "T42", value: 5 }],
      },
      {
        id: "D",
        text: "Teaching or mentoring others — helping them become the best version of themselves",
        traitWeights: [{ traitId: "T30", value: 9 }, { traitId: "T14", value: 7 }, { traitId: "T17", value: 6 }, { traitId: "T19", value: 5 }],
      },
    ],
  },
  {
    id: "Q18",
    section: 2,
    text: "When you listen to music, what naturally draws your attention?",
    options: [
      {
        id: "A",
        text: "The rhythm and mathematical structure beneath the sound",
        traitWeights: [{ traitId: "T26", value: 7 }, { traitId: "T07", value: 5 }, { traitId: "T27", value: 7 }, { traitId: "T09", value: 4 }],
      },
      {
        id: "B",
        text: "The emotion — what it makes me feel and why",
        traitWeights: [{ traitId: "T27", value: 8 }, { traitId: "T23", value: 7 }, { traitId: "T22", value: 5 }, { traitId: "T14", value: 5 }],
      },
      {
        id: "C",
        text: "The technical skill of the musicians — the precision and craft",
        traitWeights: [{ traitId: "T04", value: 6 }, { traitId: "T26", value: 6 }, { traitId: "T47", value: 5 }, { traitId: "T25", value: 5 }],
      },
      {
        id: "D",
        text: "The melody and how all the parts fit together into a whole",
        traitWeights: [{ traitId: "T26", value: 8 }, { traitId: "T27", value: 7 }, { traitId: "T21", value: 6 }, { traitId: "T06", value: 4 }],
      },
    ],
  },
  {
    id: "Q19",
    section: 2,
    text: "You see something unfair happening in your school or community. What do you do?",
    options: [
      {
        id: "A",
        text: "Speak up immediately, even if it's uncomfortable or unpopular",
        traitWeights: [{ traitId: "T28", value: 8 }, { traitId: "T42", value: 8 }, { traitId: "T17", value: 6 }, { traitId: "T33", value: 5 }],
      },
      {
        id: "B",
        text: "Think carefully about the best strategy before acting",
        traitWeights: [{ traitId: "T34", value: 7 }, { traitId: "T36", value: 6 }, { traitId: "T35", value: 6 }, { traitId: "T01", value: 5 }],
      },
      {
        id: "C",
        text: "Gather others and address it collectively — a group has more power",
        traitWeights: [{ traitId: "T33", value: 7 }, { traitId: "T19", value: 7 }, { traitId: "T18", value: 6 }, { traitId: "T17", value: 5 }],
      },
      {
        id: "D",
        text: "Document it thoroughly and find the right official channel to report it",
        traitWeights: [{ traitId: "T42", value: 8 }, { traitId: "T47", value: 6 }, { traitId: "T43", value: 7 }, { traitId: "T11", value: 5 }],
      },
    ],
  },
  {
    id: "Q20",
    section: 2,
    text: "When you make a mistake, your first response is usually:",
    options: [
      {
        id: "A",
        text: "Figure out exactly what went wrong and why — I need to understand the root cause",
        traitWeights: [{ traitId: "T01", value: 7 }, { traitId: "T43", value: 7 }, { traitId: "T47", value: 6 }, { traitId: "T45", value: 5 }],
      },
      {
        id: "B",
        text: "Forgive myself quickly and focus on what to do next",
        traitWeights: [{ traitId: "T41", value: 8 }, { traitId: "T24", value: 7 }, { traitId: "T44", value: 6 }, { traitId: "T23", value: 4 }],
      },
      {
        id: "C",
        text: "Feel bad for a while, then push myself to find the fix",
        traitWeights: [{ traitId: "T36", value: 5 }, { traitId: "T44", value: 5 }, { traitId: "T48", value: 5 }, { traitId: "T41", value: 4 }],
      },
      {
        id: "D",
        text: "Make sure I thoroughly understand what happened so I never repeat it",
        traitWeights: [{ traitId: "T48", value: 8 }, { traitId: "T31", value: 7 }, { traitId: "T41", value: 6 }, { traitId: "T47", value: 5 }],
      },
    ],
  },
  {
    id: "Q21",
    section: 2,
    text: "How do you feel about strict routines and rigid structure?",
    options: [
      {
        id: "A",
        text: "I love them — they help me stay disciplined and efficient",
        traitWeights: [{ traitId: "T48", value: 9 }, { traitId: "T38", value: 6 }, { traitId: "T47", value: 6 }, { traitId: "T31", value: 5 }],
      },
      {
        id: "B",
        text: "I like some structure but need room to improvise and adjust",
        traitWeights: [{ traitId: "T16", value: 7 }, { traitId: "T40", value: 6 }, { traitId: "T48", value: 4 }, { traitId: "T24", value: 5 }],
      },
      {
        id: "C",
        text: "I prefer total freedom — too much structure kills my creativity",
        traitWeights: [{ traitId: "T16", value: 8 }, { traitId: "T22", value: 7 }, { traitId: "T40", value: 7 }, { traitId: "T12", value: 5 }],
      },
      {
        id: "D",
        text: "It depends entirely on the task — I adapt to whatever the situation needs",
        traitWeights: [{ traitId: "T24", value: 7 }, { traitId: "T44", value: 6 }, { traitId: "T41", value: 6 }, { traitId: "T35", value: 4 }],
      },
    ],
  },
  {
    id: "Q22",
    section: 2,
    text: "Someone you care about is going through a health crisis. What do you find yourself doing?",
    options: [
      {
        id: "A",
        text: "Researching their condition thoroughly so I can ask the right questions and understand what they're facing",
        traitWeights: [{ traitId: "T45", value: 8 }, { traitId: "T11", value: 7 }, { traitId: "T14", value: 5 }, { traitId: "T43", value: 6 }],
      },
      {
        id: "B",
        text: "Sitting with them, listening, being emotionally present — they need to feel less alone",
        traitWeights: [{ traitId: "T14", value: 9 }, { traitId: "T23", value: 8 }, { traitId: "T15", value: 6 }, { traitId: "T49", value: 5 }],
      },
      {
        id: "C",
        text: "Coordinating practical help — who can visit, what meals are needed, what appointments exist",
        traitWeights: [{ traitId: "T38", value: 7 }, { traitId: "T33", value: 6 }, { traitId: "T18", value: 6 }, { traitId: "T19", value: 5 }],
      },
      {
        id: "D",
        text: "Staying positive and helping them keep their spirits up through creativity or humour",
        traitWeights: [{ traitId: "T16", value: 6 }, { traitId: "T24", value: 6 }, { traitId: "T28", value: 5 }, { traitId: "T22", value: 5 }],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// SECTION 3 — "What You Prefer"  (10 questions)
// Focus: cross-cluster disambiguation, vocational preferences, physical traits
// ---------------------------------------------------------------------------

const section3: Question[] = [
  {
    id: "Q23",
    section: 3,
    text: "You have a completely free afternoon. Which of these projects would excite you most?",
    options: [
      {
        id: "A",
        text: "Building or coding something entirely from scratch",
        traitWeights: [{ traitId: "T13", value: 8 }, { traitId: "T04", value: 7 }, { traitId: "T25", value: 5 }, { traitId: "T29", value: 5 }],
      },
      {
        id: "B",
        text: "Writing, drawing, composing music, or making a video",
        traitWeights: [{ traitId: "T22", value: 8 }, { traitId: "T16", value: 7 }, { traitId: "T21", value: 7 }, { traitId: "T26", value: 5 }],
      },
      {
        id: "C",
        text: "Deep-diving into a topic I know absolutely nothing about",
        traitWeights: [{ traitId: "T11", value: 8 }, { traitId: "T12", value: 8 }, { traitId: "T43", value: 6 }, { traitId: "T31", value: 5 }],
      },
      {
        id: "D",
        text: "Organising a group activity or community event",
        traitWeights: [{ traitId: "T33", value: 7 }, { traitId: "T38", value: 7 }, { traitId: "T18", value: 6 }, { traitId: "T17", value: 5 }],
      },
    ],
  },
  {
    id: "Q24",
    section: 3,
    text: "Which school subject do you find most naturally engaging — not just good at, but genuinely enjoy?",
    options: [
      {
        id: "A",
        text: "Mathematics, Physics, or Computer Science",
        traitWeights: [{ traitId: "T07", value: 8 }, { traitId: "T02", value: 7 }, { traitId: "T03", value: 5 }, { traitId: "T13", value: 5 }],
      },
      {
        id: "B",
        text: "Literature, History, Languages, or Art",
        traitWeights: [{ traitId: "T22", value: 7 }, { traitId: "T16", value: 6 }, { traitId: "T31", value: 6 }, { traitId: "T17", value: 6 }],
      },
      {
        id: "C",
        text: "Biology, Health Science, or Agricultural Science",
        traitWeights: [{ traitId: "T45", value: 7 }, { traitId: "T14", value: 5 }, { traitId: "T11", value: 6 }, { traitId: "T12", value: 5 }],
      },
      {
        id: "D",
        text: "Business Studies, Economics, or Government",
        traitWeights: [{ traitId: "T37", value: 8 }, { traitId: "T34", value: 6 }, { traitId: "T36", value: 5 }, { traitId: "T19", value: 5 }],
      },
    ],
  },
  {
    id: "Q25",
    section: 3,
    text: "You are most completely in your element when you are:",
    options: [
      {
        id: "A",
        text: "Working through a complex system, mechanism, or technical problem",
        traitWeights: [{ traitId: "T06", value: 8 }, { traitId: "T05", value: 7 }, { traitId: "T04", value: 6 }, { traitId: "T02", value: 5 }],
      },
      {
        id: "B",
        text: "Performing, presenting, or expressing yourself in front of others",
        traitWeights: [{ traitId: "T22", value: 8 }, { traitId: "T17", value: 7 }, { traitId: "T28", value: 7 }, { traitId: "T25", value: 5 }],
      },
      {
        id: "C",
        text: "Helping someone work through a personal or emotional challenge",
        traitWeights: [{ traitId: "T14", value: 7 }, { traitId: "T30", value: 8 }, { traitId: "T23", value: 6 }, { traitId: "T15", value: 5 }],
      },
      {
        id: "D",
        text: "Planning and then successfully executing a project from start to finish",
        traitWeights: [{ traitId: "T38", value: 8 }, { traitId: "T34", value: 7 }, { traitId: "T48", value: 6 }, { traitId: "T47", value: 5 }],
      },
    ],
  },
  {
    id: "Q26",
    section: 3,
    text: "If you could have any single superpower, which would you choose?",
    options: [
      {
        id: "A",
        text: "Instantly understand how any machine, system, or technology works",
        traitWeights: [{ traitId: "T04", value: 8 }, { traitId: "T05", value: 7 }, { traitId: "T06", value: 6 }, { traitId: "T02", value: 5 }],
      },
      {
        id: "B",
        text: "Perfectly read and understand the emotions of everyone around me",
        traitWeights: [{ traitId: "T14", value: 9 }, { traitId: "T15", value: 8 }, { traitId: "T23", value: 7 }, { traitId: "T27", value: 4 }],
      },
      {
        id: "C",
        text: "Create any artwork, invention, or structure instantly at will",
        traitWeights: [{ traitId: "T16", value: 8 }, { traitId: "T29", value: 7 }, { traitId: "T21", value: 7 }, { traitId: "T22", value: 6 }],
      },
      {
        id: "D",
        text: "Instantly see the optimal strategy in any situation",
        traitWeights: [{ traitId: "T34", value: 9 }, { traitId: "T35", value: 8 }, { traitId: "T39", value: 7 }, { traitId: "T36", value: 5 }],
      },
    ],
  },
  {
    id: "Q27",
    section: 3,
    text: "Which work environment appeals to you most?",
    options: [
      {
        id: "A",
        text: "A focused lab or technical workspace — quiet, precise, equipment-rich",
        traitWeights: [{ traitId: "T47", value: 7 }, { traitId: "T04", value: 6 }, { traitId: "T11", value: 6 }, { traitId: "T48", value: 5 }],
      },
      {
        id: "B",
        text: "A creative studio — colour, music, ideas, and constant experimentation",
        traitWeights: [{ traitId: "T21", value: 8 }, { traitId: "T16", value: 7 }, { traitId: "T22", value: 6 }, { traitId: "T26", value: 5 }],
      },
      {
        id: "C",
        text: "A busy collaborative office — people, meetings, and big decisions being made",
        traitWeights: [{ traitId: "T33", value: 6 }, { traitId: "T17", value: 6 }, { traitId: "T18", value: 6 }, { traitId: "T35", value: 5 }],
      },
      {
        id: "D",
        text: "The outdoors or the field — physically active and hands-on",
        traitWeights: [{ traitId: "T25", value: 8 }, { traitId: "T29", value: 7 }, { traitId: "T05", value: 5 }, { traitId: "T12", value: 4 }],
      },
    ],
  },
  {
    id: "Q28",
    section: 3,
    text: "When making a major personal decision, what do you rely on most?",
    options: [
      {
        id: "A",
        text: "Data, research, and careful logical analysis",
        traitWeights: [{ traitId: "T01", value: 8 }, { traitId: "T10", value: 7 }, { traitId: "T02", value: 6 }, { traitId: "T11", value: 5 }],
      },
      {
        id: "B",
        text: "Gut feeling and intuition — I trust my instincts",
        traitWeights: [{ traitId: "T35", value: 7 }, { traitId: "T40", value: 7 }, { traitId: "T28", value: 6 }, { traitId: "T23", value: 4 }],
      },
      {
        id: "C",
        text: "What feels most ethical and aligned with my values",
        traitWeights: [{ traitId: "T42", value: 9 }, { traitId: "T23", value: 6 }, { traitId: "T14", value: 5 }, { traitId: "T32", value: 5 }],
      },
      {
        id: "D",
        text: "The collective opinion of the most trusted people in my life",
        traitWeights: [{ traitId: "T18", value: 7 }, { traitId: "T14", value: 5 }, { traitId: "T17", value: 5 }, { traitId: "T49", value: 5 }],
      },
    ],
  },
  {
    id: "Q29",
    section: 3,
    text: "Which type of achievement would make you most proud in 10 years?",
    options: [
      {
        id: "A",
        text: "Solving a problem that genuinely no one else in the world had cracked",
        traitWeights: [{ traitId: "T46", value: 8 }, { traitId: "T01", value: 7 }, { traitId: "T12", value: 7 }, { traitId: "T11", value: 5 }],
      },
      {
        id: "B",
        text: "Creating something — a novel, a product, a film — that moves people",
        traitWeights: [{ traitId: "T22", value: 8 }, { traitId: "T21", value: 7 }, { traitId: "T16", value: 6 }, { traitId: "T20", value: 5 }],
      },
      {
        id: "C",
        text: "Building and leading a team that achieves something remarkable together",
        traitWeights: [{ traitId: "T33", value: 8 }, { traitId: "T38", value: 7 }, { traitId: "T39", value: 7 }, { traitId: "T42", value: 4 }],
      },
      {
        id: "D",
        text: "Making a discovery or inventing something that advances human knowledge",
        traitWeights: [{ traitId: "T11", value: 8 }, { traitId: "T12", value: 7 }, { traitId: "T04", value: 6 }, { traitId: "T13", value: 5 }],
      },
    ],
  },
  {
    id: "Q30",
    section: 3,
    text: "When working on a long-term project, what tends to be your biggest challenge?",
    options: [
      {
        id: "A",
        text: "Staying patient when progress is slow and results aren't visible yet",
        traitWeights: [{ traitId: "T49", value: 8 }, { traitId: "T48", value: 7 }, { traitId: "T41", value: 5 }, { traitId: "T44", value: 5 }],
      },
      {
        id: "B",
        text: "Staying creative and not getting bored once the novelty wears off",
        traitWeights: [{ traitId: "T16", value: 7 }, { traitId: "T12", value: 6 }, { traitId: "T40", value: 5 }, { traitId: "T22", value: 5 }],
      },
      {
        id: "C",
        text: "Keeping track of every moving detail without anything slipping through",
        traitWeights: [{ traitId: "T47", value: 8 }, { traitId: "T38", value: 6 }, { traitId: "T01", value: 5 }, { traitId: "T48", value: 5 }],
      },
      {
        id: "D",
        text: "Staying motivated and pushing through when it genuinely gets hard",
        traitWeights: [{ traitId: "T41", value: 8 }, { traitId: "T44", value: 7 }, { traitId: "T48", value: 6 }, { traitId: "T28", value: 4 }],
      },
    ],
  },
  {
    id: "Q31",
    section: 3,
    text: "You feel most energised and alive when you are:",
    options: [
      {
        id: "A",
        text: "Understanding how something works under the hood — the real mechanics of it",
        traitWeights: [{ traitId: "T12", value: 8 }, { traitId: "T04", value: 7 }, { traitId: "T06", value: 5 }, { traitId: "T05", value: 4 }],
      },
      {
        id: "B",
        text: "Connecting with people and genuinely understanding their stories",
        traitWeights: [{ traitId: "T14", value: 8 }, { traitId: "T18", value: 7 }, { traitId: "T19", value: 6 }, { traitId: "T15", value: 5 }],
      },
      {
        id: "C",
        text: "Hitting a measurable goal or milestone — seeing concrete results",
        traitWeights: [{ traitId: "T33", value: 6 }, { traitId: "T48", value: 7 }, { traitId: "T39", value: 6 }, { traitId: "T46", value: 5 }],
      },
      {
        id: "D",
        text: "Expressing or making something that has never existed before",
        traitWeights: [{ traitId: "T22", value: 8 }, { traitId: "T16", value: 7 }, { traitId: "T21", value: 6 }, { traitId: "T20", value: 5 }],
      },
    ],
  },
  {
    id: "Q32",
    section: 3,
    text: "In your ideal career, the core of your daily work is:",
    options: [
      {
        id: "A",
        text: "Solving hard technical, scientific, or engineering problems",
        traitWeights: [{ traitId: "T46", value: 7 }, { traitId: "T01", value: 7 }, { traitId: "T04", value: 6 }, { traitId: "T13", value: 6 }],
      },
      {
        id: "B",
        text: "Directly improving people's lives — health, education, wellbeing",
        traitWeights: [{ traitId: "T14", value: 8 }, { traitId: "T30", value: 7 }, { traitId: "T19", value: 7 }, { traitId: "T15", value: 5 }],
      },
      {
        id: "C",
        text: "Leading others toward a compelling shared vision",
        traitWeights: [{ traitId: "T33", value: 8 }, { traitId: "T39", value: 8 }, { traitId: "T34", value: 7 }, { traitId: "T28", value: 5 }],
      },
      {
        id: "D",
        text: "Creating things — products, art, systems — that did not exist before",
        traitWeights: [{ traitId: "T16", value: 8 }, { traitId: "T22", value: 7 }, { traitId: "T29", value: 6 }, { traitId: "T13", value: 5 }],
      },
    ],
  },
];

export const ALL_QUESTIONS: Question[] = [...section1, ...section2, ...section3];

export const SECTION_META = {
  1: {
    title: "How You Think",
    subtitle: "Questions about how you approach problems, systems, and information",
    color: "from-blue-500 to-indigo-600",
    accent: "blue",
  },
  2: {
    title: "How You Feel & Connect",
    subtitle: "Questions about emotions, people, creativity, and values",
    color: "from-rose-500 to-pink-600",
    accent: "rose",
  },
  3: {
    title: "What You Prefer",
    subtitle: "Questions about environments, activities, and the work that draws you in",
    color: "from-emerald-500 to-teal-600",
    accent: "emerald",
  },
} as const;
